// src/components/PerformanceMap/PerformanceSidebar.tsx
import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Divider,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Breadcrumbs,
  Link,
  Chip,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  AccountTree,
  ViewList,
  GridView,
  Timeline,
  ChevronRight,
  Refresh,
  School,
  FilterList,
  Clear,
} from '@mui/icons-material';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { PerformanceData } from './PerformanceViewModes';

interface PerformanceSidebarProps {
  error: string;
  setError: (error: string) => void;
  availableSubjects: string[];
  selectedSubject: string;
  setSelectedSubject: (val: string) => void;
  subjectOptions: { label: string; value: string }[];
  viewMode: 'flow' | 'tree' | 'grid' | 'list';
  setViewMode: (mode: 'flow' | 'tree' | 'grid' | 'list') => void;
  currentData: PerformanceData | undefined;
  selectedNode: 'subject' | 'topic' | 'subtopic';
  selectedTopic: string;
  selectedSubtopic: string;
  setSelectedNode: (node: 'subject' | 'topic' | 'subtopic') => void;
  setSelectedTopic: (topic: string) => void;
  setSelectedSubtopic: (subtopic: string) => void;
  setDetailContent: (content: any) => void;
  onReset: () => void;
  isMobile: boolean;
  loading: boolean;
  availableTopics: string[];
  availableSubtopics: string[];
  onTopicChange: (topic: string) => void;
  onSubtopicChange: (subtopic: string) => void;
  onClearFilters: () => void;
}

export const PerformanceSidebar: React.FC<PerformanceSidebarProps> = ({
  error,
  setError,
  availableSubjects,
  selectedSubject,
  setSelectedSubject,
  subjectOptions,
  viewMode,
  setViewMode,
  currentData,
  selectedNode,
  selectedTopic,
  selectedSubtopic,
  setSelectedNode,
  setSelectedTopic,
  setSelectedSubtopic,
  setDetailContent,
  onReset,
  isMobile,
  loading,
  availableTopics,
  availableSubtopics,
  onTopicChange,
  onSubtopicChange,
  onClearFilters,
}) => {
  // Calculate stats
  const totalTopics = currentData?.topics?.length || 0;
  const totalSubtopics = currentData?.topics?.reduce((acc, t) => acc + (t.subtopics?.length || 0), 0) || 0;
  const masteredCount = currentData?.topics?.reduce((acc, t) => {
    return acc + (t.subtopics?.filter(st => st.best_score !== null && st.best_score >= 75).length || 0);
  }, 0) || 0;
  const progress = totalSubtopics > 0 ? Math.round((masteredCount / totalSubtopics) * 100) : 0;

  const hasActiveFilters = selectedTopic || selectedSubtopic;

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <School sx={{ color: '#4f46e5' }} />
        Performance Map
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Subject Selection */}
      <Box sx={{ mb: 2 }}>
        <SearchableDropdown
          label="Select Subject"
          options={subjectOptions}
          value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
          onChange={(val) => {
            setSelectedSubject(val?.value || '');
            setSelectedTopic('');
            setSelectedSubtopic('');
            setSelectedNode('subject');
            setDetailContent(null);
          }}
          size="small"
          loading={loading}
        />
      </Box>

      {/* Topic Filter */}
      {currentData && availableTopics.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SearchableDropdown
            label="Filter by Topic"
            options={availableTopics.map((topic) => ({
              label: topic,
              value: topic,
            }))}
            value={selectedTopic ? { label: selectedTopic, value: selectedTopic } : null}
            onChange={(val) => {
              const topic = val?.value || '';
              onTopicChange(topic);
            }}
            size="small"
            disabled={!selectedSubject}
            placeholder="All Topics"
          />
        </Box>
      )}

      {/* Subtopic Filter */}
      {currentData && selectedTopic && availableSubtopics.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SearchableDropdown
            label="Filter by Subtopic"
            options={availableSubtopics.map((subtopic) => ({
              label: subtopic,
              value: subtopic,
            }))}
            value={selectedSubtopic ? { label: selectedSubtopic, value: selectedSubtopic } : null}
            onChange={(val) => {
              const subtopic = val?.value || '';
              onSubtopicChange(subtopic);
            }}
            size="small"
            disabled={!selectedTopic}
            placeholder="All Subtopics"
          />
        </Box>
      )}

      {/* Active Filters */}
      {/* {hasActiveFilters && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #c7d2fe' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList sx={{ fontSize: 16, color: '#4338ca' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#4338ca' }}>
                Filters:
              </Typography>
              {selectedTopic && (
                <Chip
                  label={`Topic: ${selectedTopic}`}
                  size="small"
                  onDelete={() => onTopicChange('')}
                  sx={{ bgcolor: '#4f46e5', color: '#fff', height: 24 }}
                />
              )}
              {selectedSubtopic && (
                <Chip
                  label={`Subtopic: ${selectedSubtopic}`}
                  size="small"
                  onDelete={() => onSubtopicChange('')}
                  sx={{ bgcolor: '#10b981', color: '#fff', height: 24 }}
                />
              )}
            </Box>
            <Button
              size="small"
              variant="text"
              startIcon={<Clear />}
              onClick={onClearFilters}
              sx={{ textTransform: 'none', color: '#4338ca' }}
            >
              Clear All
            </Button>
          </Box>
        </Paper>
      )} */}

      {/* Stats Summary */}
      {currentData && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>Progress</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#4f46e5' }}>{progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#4f46e5',
                borderRadius: 3,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Topics: {totalTopics}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Subtopics: {totalSubtopics}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Mastered: {masteredCount}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* View Mode Selector */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#64748b' }}>
        View Mode
      </Typography>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_, val) => val && setViewMode(val)}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          '& .MuiToggleButton-root': {
            flex: 1,
            minWidth: 0,
            fontSize: isMobile ? 10 : 12,
            py: 1,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            '&.Mui-selected': {
              bgcolor: '#4f46e5',
              color: '#fff',
              borderColor: '#4f46e5',
              '&:hover': {
                bgcolor: '#4338ca',
              },
            },
          },
        }}
      >
        <ToggleButton value="flow">
          <AccountTree sx={{ fontSize: isMobile ? 16 : 20, mr: 0.5 }} />
          Flow
        </ToggleButton>
        <ToggleButton value="tree">
          <Timeline sx={{ fontSize: isMobile ? 16 : 20, mr: 0.5 }} />
          Tree
        </ToggleButton>
        <ToggleButton value="grid">
          <GridView sx={{ fontSize: isMobile ? 16 : 20, mr: 0.5 }} />
          Grid
        </ToggleButton>
        <ToggleButton value="list">
          <ViewList sx={{ fontSize: isMobile ? 16 : 20, mr: 0.5 }} />
          List
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ my: 2 }} />

      {/* Navigation breadcrumb */}
      {currentData && (
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs separator={<ChevronRight fontSize="small" />}>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setSelectedNode('subject');
                setSelectedTopic('');
                setSelectedSubtopic('');
                setDetailContent(null);
              }}
              sx={{
                color: selectedNode === 'subject' ? '#4f46e5' : 'inherit',
                fontWeight: selectedNode === 'subject' ? 700 : 400,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {currentData.subject}
            </Link>
            {selectedNode !== 'subject' && selectedTopic && (
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setSelectedNode('topic');
                  setSelectedSubtopic('');
                  setDetailContent(null);
                }}
                sx={{
                  color: selectedNode === 'topic' ? '#4f46e5' : 'inherit',
                  fontWeight: selectedNode === 'topic' ? 700 : 400,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {selectedTopic}
              </Link>
            )}
            {selectedNode === 'subtopic' && selectedSubtopic && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#4f46e5' }}>
                {selectedSubtopic}
              </Typography>
            )}
          </Breadcrumbs>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {currentData && (
          <Button
            variant="outlined"
            fullWidth
            size="small"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            startIcon={<Refresh />}
            onClick={onReset}
          >
            Reset View
          </Button>
        )}
      </Box>
    </Box>
  );
};